/**
 * Media library API — mock implementation.
 *
 * The real flow:
 *   1. POST /api/v1/media/upload-url  → { mediaId, uploadUrl, fields }
 *   2. PUT directly to S3 with XHR    (progress tracked client-side)
 *   3. POST /api/v1/media/:id/complete → MediaAsset
 *   4. GET  /api/v1/media             → MediaAsset[]
 *
 * Until the backend Lambda is built this module keeps an in-memory store so
 * the full UI flow — upload, progress, gallery, archive, delete — is
 * exercisable without a deployed API.  The store is module-level, so it
 * survives re-renders but resets on page reload (which is fine for a mock).
 */

import type { MediaAsset, MediaAssetType } from '@app/common';

export type { MediaAsset, MediaAssetStatus, MediaAssetType } from '@app/common';

// ─── Limits ──────────────────────────────────────────────────────────────────

export const IMAGE_MAX_BYTES = 100 * 1024 * 1024; // 100 MB
export const VIDEO_MAX_BYTES = 10 * 1024 * 1024 * 1024; // 10 GB

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
export const ACCEPTED_MIME_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];

export function resolveMediaType(mimeType: string): MediaAssetType | null {
  if (ACCEPTED_IMAGE_TYPES.includes(mimeType)) return 'image';
  if (ACCEPTED_VIDEO_TYPES.includes(mimeType)) return 'video';
  return null;
}

// ─── In-memory store ─────────────────────────────────────────────────────────

interface StoredAsset extends MediaAsset {
  /** Blob URL — lives only for the current page session. */
  _blobUrl: string;
}

const store: StoredAsset[] = [];

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Upload helpers ───────────────────────────────────────────────────────────

export interface UploadProgressEvent {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Simulates the full S3-presigned-URL upload flow.
 *
 * In production this would be:
 *   1. apiFetch(MEDIA_UPLOAD_URL, { method: 'POST', body: ... })
 *   2. XHR PUT to the returned uploadUrl with progress tracking
 *   3. apiFetch(MEDIA_COMPLETE(mediaId), { method: 'POST' })
 *
 * Here we:
 *   1. Probe the file for dimensions/duration via the browser media APIs
 *   2. Simulate upload progress with a timer
 *   3. Add the asset to the in-memory store
 */
export async function uploadMediaFile(
  file: File,
  ownerId: string,
  ownerEmail: string | null,
  onProgress: (e: UploadProgressEvent) => void,
  signal: AbortSignal
): Promise<MediaAsset> {
  const type = resolveMediaType(file.type);
  if (!type) throw new Error('Unsupported file type');

  const limit = type === 'image' ? IMAGE_MAX_BYTES : VIDEO_MAX_BYTES;
  if (file.size > limit) throw new Error('FILE_TOO_LARGE');

  // Create a stable blob URL for this session.
  const blobUrl = URL.createObjectURL(file);

  // Probe dimensions/duration from the browser.
  const meta = await probeFileMeta(file, blobUrl, type);

  // Simulate upload progress over ~1-2 seconds.
  await simulateProgress(file.size, onProgress, signal);

  if (signal.aborted) {
    URL.revokeObjectURL(blobUrl);
    throw new DOMException('Upload cancelled', 'AbortError');
  }

  const asset: StoredAsset = {
    id: uid(),
    ownerId,
    ownerEmail,
    name: file.name,
    mimeType: file.type,
    type,
    sizeBytes: file.size,
    url: blobUrl,
    thumbnailUrl: type === 'image' ? blobUrl : null,
    width: meta.width,
    height: meta.height,
    durationMs: meta.durationMs,
    altText: null,
    status: 'ready',
    sharedWith: [],
    createdAt: new Date().toISOString(),
    _blobUrl: blobUrl,
  };

  store.unshift(asset);
  return asset;
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

export function listMedia(viewerId: string, isAdmin: boolean): MediaAsset[] {
  return store.filter((a) => {
    if (isAdmin) return true;
    // Creator sees own assets + anything explicitly shared with them.
    return a.ownerId === viewerId || a.sharedWith.includes(viewerId);
  });
}

export function archiveMedia(id: string, viewerId: string, isAdmin: boolean): void {
  const asset = store.find((a) => a.id === id);
  if (!asset) throw new Error('NOT_FOUND');
  if (!isAdmin && asset.ownerId !== viewerId) throw new Error('FORBIDDEN');
  asset.status = 'archived';
}

export function restoreMedia(id: string, viewerId: string, isAdmin: boolean): void {
  const asset = store.find((a) => a.id === id);
  if (!asset) throw new Error('NOT_FOUND');
  if (!isAdmin && asset.ownerId !== viewerId) throw new Error('FORBIDDEN');
  asset.status = 'ready';
}

export function deleteMedia(id: string, viewerId: string, isAdmin: boolean): void {
  const idx = store.findIndex((a) => a.id === id);
  if (idx === -1) throw new Error('NOT_FOUND');
  const asset = store[idx];
  if (!isAdmin && asset.ownerId !== viewerId) throw new Error('FORBIDDEN');
  URL.revokeObjectURL(asset._blobUrl);
  store.splice(idx, 1);
}

/** Admin only: grant a creator access to a specific asset. */
export function shareMedia(id: string, targetUserId: string): void {
  const asset = store.find((a) => a.id === id);
  if (!asset) throw new Error('NOT_FOUND');
  if (!asset.sharedWith.includes(targetUserId)) {
    asset.sharedWith.push(targetUserId);
  }
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

interface FileMeta {
  width: number | null;
  height: number | null;
  durationMs: number | null;
}

function probeFileMeta(_file: File, blobUrl: string, type: MediaAssetType): Promise<FileMeta> {
  return new Promise((resolve) => {
    if (type === 'image') {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight, durationMs: null });
      img.onerror = () => resolve({ width: null, height: null, durationMs: null });
      img.src = blobUrl;
    } else {
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () =>
        resolve({
          width: vid.videoWidth || null,
          height: vid.videoHeight || null,
          durationMs: isFinite(vid.duration) ? Math.round(vid.duration * 1000) : null,
        });
      vid.onerror = () => resolve({ width: null, height: null, durationMs: null });
      vid.src = blobUrl;
    }
  });
}

/** Simulate an upload by emitting progress events over a realistic duration. */
function simulateProgress(
  totalBytes: number,
  onProgress: (e: UploadProgressEvent) => void,
  signal: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Scale duration: ~1 s for a small file, up to 3 s for large.
    const durationMs = Math.min(3000, 500 + (totalBytes / (1024 * 1024)) * 100);
    const steps = 20;
    const stepMs = durationMs / steps;
    let step = 0;

    const tick = setInterval(() => {
      if (signal.aborted) {
        clearInterval(tick);
        reject(new DOMException('Upload cancelled', 'AbortError'));
        return;
      }
      step++;
      const loaded = Math.round((step / steps) * totalBytes);
      onProgress({ loaded, total: totalBytes, percent: Math.round((step / steps) * 100) });
      if (step >= steps) {
        clearInterval(tick);
        resolve();
      }
    }, stepMs);
  });
}
